import {
  AttachmentObjectDescription,
  BiterampObjectDescription,
  TranslateBlockObjectDescription,
  ElasticObjectDescription,
  GingivaObjectDescription,
  ICbct,
  IIPR,
  IJaw,
  IOcclusionFeatureConfig,
  IPRObjectDescription,
  IStage,
  IToothlikeOcclusion,
  JawType,
  ObjectDescription,
  PonticObjectDescription,
  ToothObjectDescription, ObjectTypes
} from '@data/models';
import {Dictionary} from '@types';
import {cloneDeep} from 'lodash';
import {getNameByFile} from './getNameByFile';
import {
  getOcclusionDistancesFromFilename,
} from '@data/providers/SceneDataProvider/utils/getOcclusions';
import {RawFile} from '@data/providers/SceneDataProvider/types';
import {ConfigProvider} from '@data/providers';
import {Matrix4} from 'three';
import {
  computeVersionedTransform
} from './getTransform';


const fillJawObjects = async (
  jaw: IJaw,
  objects: Dictionary<ObjectDescription>,
  jawType: JawType,
  stageId: string,
  fileNames: string[],
  occlusions: RawFile[],
  formatVersion: number,
  configProvider?: ConfigProvider,
  cbct?: ICbct,
  stageJawDisplacement?: any
) => {
  const gingivaName = getNameByFile(fileNames[jaw.gingiva.file]);
  const gingivaObject = objects[gingivaName] as GingivaObjectDescription;
  gingivaObject.visible = true;
  gingivaObject.transform = jaw.gingiva.transform;
  gingivaObject.jaw = jawType;
  gingivaObject.jawDisplacementTransform = stageJawDisplacement?.transform || null;

  const getObjectTransform = (transform: number[]) => computeVersionedTransform(transform, jaw.gingiva.transform, formatVersion);

  const occlusionsConfig = configProvider?.getFeatureConfig<IOcclusionFeatureConfig>("occlusions")
  const cbctJawFilename = jawType === JawType.Upper ? (cbct?.upper ?? cbct?.Upper) : (cbct?.lower ?? cbct?.Lower);
  if (cbctJawFilename) {
    const cbctJawName = getNameByFile(fileNames[cbctJawFilename!]);
    const cbctJawObject = objects[cbctJawName];
    cbctJawObject.visible = true;
    cbctJawObject.transform = jaw.gingiva.transform;
    cbctJawObject.jaw = jawType;
  }

  for (const tooth of jaw.teeth) {
    const toothName = getNameByFile(fileNames[tooth.file]);
    const toothObject = objects[toothName] as ToothObjectDescription;
    toothObject.visible = true;
    toothObject.transform = getObjectTransform(tooth.transform);
    toothObject.jaw = jawType;

    // Handle regular occlusion
    if (tooth.occlusion) {
      toothObject.occlusionDistances = await getOcclusionDistancesFromFilename(tooth.occlusion, occlusions, occlusionsConfig || undefined);
    }

    // Handle jaw displacement occlusion
    if (stageJawDisplacement?.occlusions) {
      const jdOcclusion = stageJawDisplacement.occlusions.find((occ: IToothlikeOcclusion) => occ.toothlikeId === tooth.id);
      if (jdOcclusion) {
        toothObject.jdOcclusionDistances = await getOcclusionDistancesFromFilename(jdOcclusion.occlusion, occlusions, occlusionsConfig || undefined);
      }
    }

    const rootName = toothName.replace('tooth-', 'root-');
    const rootObject = objects[rootName];
    if (rootObject) {
      rootObject.visible = true;
      rootObject.transform = getObjectTransform(tooth.transform);
      rootObject.jaw = jawType;
    }
  }

  if (jaw.attachments) {
    jaw.attachments.forEach((attachment: any) => {
      const attachmentName = getNameByFile(fileNames[attachment.file]);
      const attachmentObject = objects[attachmentName] as AttachmentObjectDescription;
      attachmentObject.visible = true;
      attachmentObject.jaw = jawType;
      attachmentObject.transform = getObjectTransform(attachment.transform);
      attachmentObject.toothId = attachment.number;
    });
  }

  if (jaw.elastics) {
    jaw.elastics.forEach((elastic) => {
      const elasticName = `${elastic.id}--${stageId}`;
      const elasticObject = objects[elasticName] as ElasticObjectDescription;
      elasticObject.visible = true;
      elasticObject.jaw = jawType;
      elasticObject.toothId = elastic.number;
      elasticObject.transform = getObjectTransform(new Matrix4().toArray());
    });
  }

  if (jaw.biteRamps) {
    jaw.biteRamps.forEach((biteRamp: any) => {
      const biterampName = getNameByFile(fileNames[biteRamp.file]);
      const biterampObject = objects[biterampName] as BiterampObjectDescription;
      biterampObject.visible = true;
      biterampObject.jaw = jawType;
      biterampObject.transform = getObjectTransform(biteRamp.transform);
      biterampObject.toothId = biteRamp.number;
    });
  }

  if (jaw.translateBlocks) {
    jaw.translateBlocks.forEach((translateBlock: any) => {
      const translateBlockName = getNameByFile(fileNames[translateBlock.file]);
      const translateBlockObject = objects[translateBlockName] as TranslateBlockObjectDescription;
      translateBlockObject.visible = true;
      translateBlockObject.jaw = jawType;
      translateBlockObject.transform = getObjectTransform(translateBlock.transform);
      translateBlockObject.toothId = translateBlock.number;
    });
  }

  if (jaw.pontics) {
    jaw.pontics.forEach(async (pontic: any) => {
      const ponticName = getNameByFile(fileNames[pontic.file]);
      const ponticObject = objects[ponticName] as PonticObjectDescription;
      ponticObject.visible = true;
      ponticObject.jaw = jawType;
      ponticObject.transform = getObjectTransform(pontic.transform);
      ponticObject.toothId = pontic.number;

      // Handle regular occlusion
      if (pontic.occlusion) {
        ponticObject.occlusionDistances = await getOcclusionDistancesFromFilename(pontic.occlusion, occlusions, occlusionsConfig || undefined);
      }

      // Handle jaw displacement occlusion
      if (stageJawDisplacement?.occlusions) {
        const jdOcclusion = stageJawDisplacement.occlusions.find((occ: IToothlikeOcclusion) => occ.toothlikeId === pontic.id);
        if (jdOcclusion) {
          ponticObject.jdOcclusionDistances = await getOcclusionDistancesFromFilename(jdOcclusion.occlusion, occlusions, occlusionsConfig || undefined);
        }
      }
    });
  }

  if (jaw.iprs) {
    jaw.iprs
      .forEach((ipr: IIPR, index: number) => {
        if ((ipr.appliedOnStage + 1) <= parseInt(stageId, 10)) {
          const firstToothName = getNameByFile(fileNames[ipr.adjacentTeethFiles[0]]);
          const secondToothName = getNameByFile(fileNames[ipr.adjacentTeethFiles[1]]);

          const iprName = `ipr-${jawType === JawType.Upper ? 'u': 'l'}-${stageId}-${index + 1}`;
          const iprObject = objects[iprName] as IPRObjectDescription;

          iprObject.transform = getObjectTransform(cloneDeep(objects[firstToothName].transform)!);
          iprObject.jaw = jawType;
          iprObject.visible = true;
          iprObject.firstToothName = firstToothName;
          iprObject.secondToothName = secondToothName;
        }
      });
  }
};

const getStageSpecificObjects = (allObjects: Dictionary<ObjectDescription>) => {
  return Object.values(allObjects).filter((obj) => {
    if (obj.type === ObjectTypes.TranslateBlock ||
      obj.type === ObjectTypes.Attachment ||
      obj.type === ObjectTypes.Biteramp ||
      obj.type === ObjectTypes.Pontic ||
      obj.type === ObjectTypes.Elastic) {
      return obj.visible;
    }
    return true;
  });
}

export const getObjectsForStage = async (
  stage: IStage,
  allObjects: Dictionary<ObjectDescription>,
  fileNames: string[],
  occlusions: RawFile[],
  formatVersion: number = 1,
  configProvider?: ConfigProvider,
  cbct?: ICbct,
): Promise<ObjectDescription[]> => {
  const allStagesObjects = cloneDeep(allObjects);
  if (stage.enabled && stage.upper && stage.lower) {
    await fillJawObjects(stage.upper, allStagesObjects, JawType.Upper, stage.id, fileNames, occlusions, formatVersion, configProvider, cbct, stage.jawDisplacement);
    await fillJawObjects(stage.lower, allStagesObjects, JawType.Lower, stage.id, fileNames, occlusions, formatVersion, configProvider, cbct, stage.jawDisplacement);
  }
  // TODO: fillJawObjects should somehow filter out objects from other stages
  return getStageSpecificObjects(allStagesObjects);
};
